'use client'

import React, { Suspense, memo, useCallback, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { twMerge } from 'tailwind-merge'

import {
  useRecipientSectionQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { RankingUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

type IndustryTab = 'naics' | 'psc'

interface IndustryCode {
  code: string
  name: string
  amount: number
  percentage: number
}

/**
 * Format currency with appropriate suffix (B, M, K)
 */
function formatCurrency(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(1)}B`
  }
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`
  }
  return `$${amount.toFixed(0)}`
}

// Hoisted static row class (rendering-hoist-jsx)
const ROW_CLASS =
  'group relative flex items-center px-2 py-1 transition-colors hover:bg-primary-50 dark:hover:bg-primary-800/50'

/**
 * RecipientDetail.Industry
 *
 * Data-table-first display with inline bar indicators for NAICS and PSC codes.
 * Prioritizes scannability over visual charts.
 */
export function IndustrySection() {
  const { recipientId } = useRecipientDetail()
  const [activeTab, setActiveTab] = useState<IndustryTab>('naics')

  // Stable callbacks to avoid re-renders (rerender-functional-setstate)
  const showNaics = useCallback(() => setActiveTab('naics'), [])
  const showPsc = useCallback(() => setActiveTab('psc'), [])

  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear

  // Prefetch dashboard data for NAICS tab
  const { data: dashboard } = useSuspenseRecipientDashboard(
    recipientId,
    fiscalYear,
  )

  return (
    <SectionCard
      id="industry"
      title="Industry Codes"
      animationDelay={150}
      headerAction={
        <div className="flex items-center gap-1 rounded-lg bg-primary-100 p-0.5 dark:bg-primary-800">
          <TabButton active={activeTab === 'naics'} onClick={showNaics}>
            NAICS
          </TabButton>
          <TabButton active={activeTab === 'psc'} onClick={showPsc}>
            PSC
          </TabButton>
        </div>
      }
    >
      <QueryErrorBoundary
        title="Failed to load industry data"
        serviceUnavailableFallback={
          <RankingUnavailable height={280} rows={5} />
        }
      >
        <Suspense fallback={<SectionSkeleton height={280} bars={5} />}>
          {activeTab === 'naics' ? (
            dashboard.topNaics === null ? (
              <DeferredNaics id={recipientId} />
            ) : (
              <NaicsContent naicsCodes={dashboard.topNaics} />
            )
          ) : (
            <PscContent id={recipientId} activeTab={activeTab} />
          )}
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
        active
          ? 'bg-white text-primary-900 shadow-sm dark:bg-primary-700 dark:text-primary-100'
          : 'text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-200',
      )}
    >
      {children}
    </button>
  )
}

function DeferredNaics({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: naicsCodes, isLoading } = useQuery(
    queries.analyticsNaics(id, fiscalYear),
  )

  if (isLoading) return <SectionSkeleton height={280} bars={5} />
  if (!naicsCodes || naicsCodes.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-primary-400 dark:text-primary-500">
        No NAICS codes found
      </div>
    )
  }

  return (
    <IndustryDataTable
      items={naicsCodes}
      codeLabel="NAICS"
      accentColor="teal"
    />
  )
}

function NaicsContent({ naicsCodes }: { naicsCodes: IndustryCode[] }) {
  if (naicsCodes.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-primary-400 dark:text-primary-500">
        No NAICS codes found
      </div>
    )
  }

  return (
    <IndustryDataTable
      items={naicsCodes}
      codeLabel="NAICS"
      accentColor="teal"
    />
  )
}

function PscContent({ id, activeTab }: { id: string; activeTab: IndustryTab }) {
  const queries = useRecipientSectionQueries()
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  // Lazy-load PSC data only when PSC tab is active
  const { data: pscCodes } = useQuery({
    ...queries.analyticsPsc(id, fiscalYear),
    enabled: activeTab === 'psc',
  })

  if (!pscCodes || pscCodes.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-primary-400 dark:text-primary-500">
        No PSC codes found
      </div>
    )
  }

  return (
    <IndustryDataTable items={pscCodes} codeLabel="PSC" accentColor="indigo" />
  )
}

/**
 * Memoized row component to prevent unnecessary re-renders (rerender-memo)
 */
const IndustryRow = memo(function IndustryRow({
  item,
  index,
  barWidth,
  barColorClass,
  codeColorClass,
}: {
  item: IndustryCode
  index: number
  barWidth: number
  barColorClass: string
  codeColorClass: string
}) {
  const pct = Number(item.percentage)

  return (
    <div className={ROW_CLASS}>
      {/* Background bar indicator */}
      <div
        className={twMerge(
          'absolute inset-y-0 left-0 transition-all',
          barColorClass,
        )}
        style={{ width: `${barWidth}%` }}
      />

      {/* Content (above the bar) */}
      <span className="relative w-6 shrink-0 text-center text-[11px] text-primary-400 dark:text-primary-500">
        {index + 1}
      </span>
      <span
        className={twMerge(
          'relative w-14 shrink-0 font-mono text-[11px] font-medium',
          codeColorClass,
        )}
      >
        {item.code}
      </span>
      <span
        className="relative min-w-0 flex-1 truncate pr-2 text-[13px] text-primary-700 dark:text-primary-300"
        title={item.name}
      >
        {item.name}
      </span>
      <span className="relative w-16 shrink-0 text-right font-mono text-[11px] text-primary-600 dark:text-primary-400">
        {formatCurrency(Number(item.amount))}
      </span>
      <span className="relative w-12 shrink-0 text-right font-mono text-[11px] font-medium text-primary-900 dark:text-primary-100">
        {pct.toFixed(1)}%
      </span>
    </div>
  )
})

/**
 * Data table with inline bar indicators
 * Designed for scannability with code, description, amount, and percentage in columns
 */
function IndustryDataTable({
  items,
  codeLabel,
  accentColor,
}: {
  items: IndustryCode[]
  codeLabel: string
  accentColor: 'teal' | 'indigo'
}) {
  // Memoize computed values (rerender-memo)
  const maxPercentage = useMemo(
    () => Math.max(...items.map((i) => Number(i.percentage)), 1),
    [items],
  )

  // Derive color classes from accent (rerender-derived-state-no-effect)
  const barColorClass =
    accentColor === 'teal'
      ? 'bg-teal-500/20 dark:bg-teal-500/30'
      : 'bg-indigo-500/20 dark:bg-indigo-500/30'

  const codeColorClass =
    accentColor === 'teal'
      ? 'text-teal-700 dark:text-teal-400'
      : 'text-indigo-700 dark:text-indigo-400'

  return (
    <div className="overflow-hidden">
      {/* Header row */}
      <div className="flex items-center border-b border-primary-200 bg-primary-50/50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-400">
        <span className="w-6 shrink-0 text-center">#</span>
        <span className="w-14 shrink-0">{codeLabel}</span>
        <span className="min-w-0 flex-1">Description</span>
        <span className="w-16 shrink-0 text-right">Amount</span>
        <span className="w-12 shrink-0 text-right">Share</span>
      </div>

      {/* Data rows - scrollable, compact */}
      <div className="max-h-[320px] divide-y divide-primary-100 overflow-y-auto dark:divide-primary-800">
        {items.map((item, index) => {
          const pct = Number(item.percentage)
          const barWidth = Math.max((pct / maxPercentage) * 100, 4)

          return (
            <IndustryRow
              key={`${item.code}-${index}`}
              item={item}
              index={index}
              barWidth={barWidth}
              barColorClass={barColorClass}
              codeColorClass={codeColorClass}
            />
          )
        })}
      </div>

      {/* Footer with count */}
      {items.length > 8 && (
        <div className="border-t border-primary-200 bg-primary-50/30 px-3 py-1.5 text-center text-xs text-primary-500 dark:border-primary-700 dark:bg-primary-800/30 dark:text-primary-400">
          {items.length} codes total
        </div>
      )}
    </div>
  )
}
